import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, ShieldCheck, Ruler, Palette, Settings, Hammer } from 'lucide-react';
import { Button } from '../components/ui/button';

const ProductPage = () => {
  const features = [
    {
      icon: Ruler,
      title: 'Bis 1,8 m Durchmesser',
      description: 'Vollkugeln in Großformaten, die am Markt kaum verfügbar sind.',
    },
    {
      icon: Settings,
      title: 'Präzisionsfertigung',
      description: 'Geometrisch exakt rund – ohne Verzug, ohne Abflachungen.',
    },
    {
      icon: ShieldCheck,
      title: 'DIN 4102 B1',
      description: 'Schwer entflammbares Material, optional A1-Beschichtung.',
    },
    {
      icon: Palette,
      title: 'Hauseigene Lackiererei',
      description: 'Professionelle Veredelung in jeder gewünschten Farbe.',
    },
  ];

  const specifications = [
    { label: 'Durchmesser', value: '600 – 1.800 mm' },
    { label: 'Raumgewicht Standard', value: '35 kg/m³' },
    { label: 'Material', value: 'EPS (Modellbaustyropor)' },
    { label: 'Brandschutz', value: 'B1 (schwer entflammbar)' },
    { label: 'Optional', value: 'A1-Beschichtung (nicht brennbar)' },
  ];

  const options = [
    'Geschliffene Vollkugeln',
    'Halbkugeln (2-teilig)',
    'Abgeflachte Standfläche',
    'Flugösen zum Anhängen',
    'Montageplatten/-ringe zum Aufstellen',
    'Individuelle Lackierung',
    'A1-Beschichtung',
    'Niedrigere Raumgewichte',
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-16 md:py-24">
        <div className="absolute inset-0 hero-glow opacity-50" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-3xl">
            <p className="text-label text-primary mb-4">Über unsere Produkte</p>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight text-white leading-tight mb-6">
              EPS-Kugeln bis <span className="text-primary">1,8 m</span> Durchmesser
            </h1>
            <p className="text-base md:text-lg text-zinc-400 leading-relaxed">
              Präzisionsgefertigte Vollkugeln für anspruchsvolle Projekte in Bühnenbildern, 
              Theater- und Opernproduktionen, Messe- und Eventdekorationen sowie Raum- und Objektinszenierungen.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24 bg-zinc-900/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Left: Description */}
            <div className="space-y-8">
              <div>
                <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-4">
                  Was uns unterscheidet
                </h2>
                <div className="space-y-4 text-zinc-400 leading-relaxed">
                  <p>
                    Kleine, handelsübliche EPS-Formteile in Kugelform bis etwa 500 mm Durchmesser sind weit verbreitet. 
                    Diese Kugeln werden in der Regel als Hohlkugeln angeboten, bestehen meist aus EPS mit niedriger 
                    Rohdichte (PS 12 oder darunter) und stammen überwiegend aus dem Hobby- und Bastelbedarf. 
                    Technische Datenblätter oder geprüfte Qualitätsnachweise sind dafür häufig nicht verfügbar.
                  </p>
                  <p className="text-white font-medium">
                    Was es hingegen kaum gibt, sind wirklich präzise Kugeln in großen Formaten, 
                    die definierte technische Eigenschaften erfüllen!
                  </p>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-4">
                  Unser Fertigungsverfahren
                </h2>
                <div className="space-y-4 text-zinc-400 leading-relaxed">
                  <p>
                    Unsere EPS-Kugeln werden in einem speziell entwickelten Fertigungsverfahren hergestellt. 
                    Das Ergebnis sind geometrisch exakt runde Vollkugeln – ohne Verzug, ohne Abflachungen 
                    und ohne die typischen Unregelmäßigkeiten heißdrahtgeschnittener oder konventionell 
                    formgeschäumter Großteile.
                  </p>
                  <p>
                    Die Kugeln rollen wie Bälle und wirken selbst bei sehr großen Durchmessern ruhig, 
                    gleichmäßig und harmonisch.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-4">
                  Einsatzbereiche
                </h2>
                <p className="text-zinc-400 leading-relaxed mb-4">
                  Wir fertigen Vollkugeln mit Durchmessern bis 1,8 m. Sie sind formstabil und eignen sich ideal für:
                </p>
                <ul className="space-y-2">
                  {['Bühnenbilder', 'Theater- und Opernproduktionen', 'Messe- und Eventdekorationen', 'Raum- und Objektinszenierungen', 'Aktionen und Ausstellungen', 'Interaktive Installationen'].map((item) => (
                    <li key={item} className="flex items-center gap-3 text-zinc-300">
                      <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Right: Specs & Options */}
            <div className="space-y-8">
              {/* Specifications */}
              <div className="bg-zinc-900/50 border border-zinc-800 p-6 rounded-sm">
                <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-6">
                  Technische Daten
                </h3>
                <div className="space-y-4">
                  {specifications.map((spec) => (
                    <div key={spec.label} className="flex justify-between items-start border-b border-zinc-800 pb-3 last:border-0 last:pb-0">
                      <span className="text-zinc-500">{spec.label}</span>
                      <span className="text-white font-medium text-right">{spec.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Fire Protection Info */}
              <div className="bg-primary/10 border border-primary/30 p-6 rounded-sm">
                <div className="flex items-start gap-4">
                  <ShieldCheck className="h-8 w-8 text-primary flex-shrink-0" />
                  <div>
                    <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-white mb-2">
                      Brandschutz-Zertifizierung
                    </h3>
                    <p className="text-zinc-400 text-sm leading-relaxed">
                      Alle unsere Objekte sind aus schwer entflammbarem Modellbaustyropor gefertigt und 
                      entsprechen der <strong className="text-white">DIN 4102 B1</strong>. Optional kann eine 
                      Beschichtung verwendet werden, um das Material auf den <strong className="text-white">A1 Level</strong> (nicht brennbar) zu bringen.
                    </p>
                  </div>
                </div>
              </div>

              {/* Options */}
              <div className="bg-zinc-900/50 border border-zinc-800 p-6 rounded-sm">
                <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-6">
                  Verfügbare Optionen
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {options.map((option) => (
                    <div key={option} className="flex items-center gap-2 text-sm text-zinc-300">
                      <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                      <span>{option}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Additional Products */}
              <div className="bg-zinc-900/50 border border-zinc-800 p-6 rounded-sm">
                <div className="flex items-start gap-4">
                  <Hammer className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-white mb-2">
                      Weitere Formteile
                    </h3>
                    <p className="text-zinc-400 text-sm leading-relaxed">
                      Neben Kugeln lassen sich mit unserem Verfahren auch andere rotationssymmetrische 
                      EPS-Formteile herstellen, z. B. Eier, Eiformen sowie Ostereier für Aktionen, 
                      Ausstellungen oder interaktive Installationen.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className="bg-zinc-900/50 border border-zinc-800 p-6 rounded-sm hover:border-zinc-700 transition-all duration-300 hover-lift"
                data-testid={`product-feature-${index}`}
              >
                <feature.icon className="h-10 w-10 text-primary mb-4" />
                <h3 className="font-heading text-xl font-semibold uppercase tracking-tight text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-zinc-900/30">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white mb-6">
            Jetzt Ihre Kugel konfigurieren
          </h2>
          <p className="text-zinc-400 mb-8">
            Nutzen Sie unseren Konfigurator, um Ihre individuelle Styropor®kugel zusammenzustellen 
            und ein unverbindliches Angebot zu erhalten.
          </p>
          <Link to="/configurator">
            <Button 
              className="h-14 px-10 bg-primary text-black font-bold uppercase tracking-wider rounded-sm hover:bg-primary/90 btn-active text-lg"
              data-testid="product-cta-button"
            >
              Zum Konfigurator
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProductPage;
